import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { ordersApi } from "components/Apis/orders";

const normalizeError = (errorPayload) => {
    if (!errorPayload) return "Unknown error";
    if (typeof errorPayload === "string") return errorPayload;
    if (typeof errorPayload?.message === "string") return errorPayload.message;
    if (typeof errorPayload?.detail === "string") return errorPayload.detail;
    if (typeof errorPayload?.msg === "string") return errorPayload.msg;
    try {
        return JSON.stringify(errorPayload);
    } catch {
        return "Unknown error";
    }
};

export const fetchOrder = createAsyncThunk(
    'logistics/fetchOrder',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await ordersApi.getById(orderId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchOrderLine = createAsyncThunk(
    'logistics/fetchOrderLine',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await ordersApi.getOrderLine(orderId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchOrderPositionUpdates = createAsyncThunk(
    'logistics/fetchOrderPositionUpdates',
    async ({ orderId, page = 1, page_size = 10 }, { rejectWithValue }) => {
        try {
            const response = await ordersApi.getPositionUpdates(orderId, page, page_size);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const specificOrderSlice = createSlice({
    name: 'specificOrder',
    initialState: {
        order: {
            data: null,
            error: null,
            loading: false,
        },
        orderLine: {
            data: null,
            error: null,
            loading: false,
        },
        positionUpdates: {
            data: null,
            error: null,
            loading: false,
            next: null,
        },
    },
    reducers: {
        updateSpecificOrder(state, action) {
            state.order = { ...state.order, ...action.payload };
        },
        resetOrderPositionUpdates(state) {
            state.positionUpdates = {
                data: null,
                error: null,
                loading: false,
                next: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrder.pending, (state) => {
                state.order.loading = true;
                state.order.error = null;
            })
            .addCase(fetchOrder.fulfilled, (state, action) => {
                state.order.loading = false;
                state.order.data = action.payload; // Store the fetched order
            })
            .addCase(fetchOrder.rejected, (state, action) => {
                state.order.loading = false;
                state.order.error = normalizeError(action.payload); // Store error message
            })
            .addCase(fetchOrderLine.pending, (state) => {
                state.orderLine.loading = true;
                state.orderLine.error = null;
            })
            .addCase(fetchOrderLine.fulfilled, (state, action) => {
                state.orderLine.loading = false;
                state.orderLine.data = action.payload; // Store the fetched order line
            })
            .addCase(fetchOrderLine.rejected, (state, action) => {
                state.orderLine.loading = false;
                state.orderLine.error = normalizeError(action.payload); // Store error message
            })
            .addCase(fetchOrderPositionUpdates.pending, (state) => {
                state.positionUpdates.loading = true;
                state.positionUpdates.error = null;
            })
            .addCase(fetchOrderPositionUpdates.fulfilled, (state, action) => {
                state.positionUpdates.loading = false;
                // Append new position updates to existing ones for pagination
                if (state.positionUpdates.data) {
                    state.positionUpdates.data = [
                        ...state.positionUpdates.data,
                        ...action.payload.results
                    ];
                } else {
                    state.positionUpdates.data = action.payload.results;
                }
                state.positionUpdates.next = action.payload.next;
            })
            .addCase(fetchOrderPositionUpdates.rejected, (state, action) => {
                state.positionUpdates.loading = false;
                state.positionUpdates.error = normalizeError(action.payload); // Store error message
            });
    },
});

export const { updateSpecificOrder, resetOrderPositionUpdates } = specificOrderSlice.actions;
export default specificOrderSlice.reducer;
